'use client';

import { motion } from 'framer-motion';
import { PortableText } from '@portabletext/react';
import { richTextComponent } from '@/sanity/sanityPortableText';
import {
  PageHowItWorksSchema,
  HowItWorksInstantFeature,
} from '@/sanity/pages/PageHowItWorksTemplate';
import { GroqImage } from '@/helpers/Image';
import dotsGradientBg from '@/public/global/dots-gradient-bg.png';
import Image from 'next/image';

const ease = [0.25, 0.1, 0.25, 1] as const;

function FeatureCard({
  label,
  icon,
  index,
}: HowItWorksInstantFeature & { index: number }) {
  const colonIdx = label?.indexOf(': ') ?? -1;
  const boldPart = colonIdx > -1 ? label!.slice(0, colonIdx + 1) : label;
  const regularPart = colonIdx > -1 ? label!.slice(colonIdx + 2) : undefined;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, ease, delay: index * 0.06 }}
      className="flex items-center gap-[2.75rem] bg-white border border-[rgba(0,0,0,0.1)] rounded-[0.25rem] p-[2rem]"
    >
      <div className="flex-shrink-0 w-[7.25rem] h-[7.25rem] bg-[#103037] rounded-[0.25rem] flex items-center justify-center overflow-hidden">
        {icon && (
          <GroqImage
            src={icon}
            alt=""
            className="!w-[3.75rem] !h-[3.75rem] !object-contain"
          />
        )}
      </div>
      <div className="flex flex-col">
        {boldPart && (
          <p className="mb-0 font-medium text-[2rem] leading-[1.4] tracking-[-0.1rem] bg-gradient-to-b from-black to-[#666666] bg-clip-text text-transparent">
            {boldPart}
          </p>
        )}
        {regularPart && (
          <p className="mb-0 font-medium text-[2rem] leading-[1.4] tracking-[-0.1rem] bg-gradient-to-b from-black to-[#666666] bg-clip-text text-transparent">
            {regularPart}
          </p>
        )}
      </div>
    </motion.div>
  );
}

export default function InstantFundingSection({
  data,
}: {
  data: PageHowItWorksSchema;
}) {
  if (!data?.instantTitle && !data?.instantFeatures?.length) return null;

  const whyFeatures = data.instantFeatures?.slice(0, 6) ?? [];
  const idealFeatures = data.instantFeatures?.slice(6) ?? [];

  return (
    <section className="relative w-full pb-0 pt-[5rem] lg:py-[16rem]">
      <div className="absolute w-[calc(100%+50rem)] lg:w-full h-[48rem] top-0 left-1/2 -translate-x-1/2 mix-blend-exclusion pointer-events-none select-none">
        <Image fill alt="Background pattern" src={dotsGradientBg} />
      </div>
      <div className="mx-auto w-[min(170.5rem,calc(100%-4rem))] lg:w-[min(170.5rem,calc(100%-8rem))] flex flex-col gap-[8rem]">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease }}
          className="flex flex-col items-center gap-[2rem] text-center"
        >
          {data?.instantChip && (
            <div className="bg-[#228e71] px-[2rem] py-[1.25rem] rounded-[0.25rem]">
              <p className="mb-0 font-geist-mono font-medium text-[2rem] text-white uppercase tracking-[0.06em]">
                {data.instantChip}
              </p>
            </div>
          )}
          {data?.instantTitle && (
            <div className="richtext  max-w-[72rem] flex flex-col gap-[2rem]">
              <PortableText
                value={data.instantTitle}
                components={richTextComponent}
              />
            </div>
          )}
        </motion.div>

        {/* Why Traders group */}
        {whyFeatures.length > 0 && (
          <div className="flex flex-col gap-[4rem]">
            <motion.h3
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, ease }}
              className="mb-0 text-[3rem] text-center font-medium leading-[1.2] tracking-[-0.15rem] text-black"
            >
              {data.instantWhyTitle ?? 'Why Traders Pick Instant Funding'}
            </motion.h3>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-[2rem]">
              {whyFeatures.map((feature, i) => (
                <FeatureCard
                  key={feature._key ?? i}
                  label={feature.label}
                  icon={feature.icon}
                  index={i}
                />
              ))}
            </div>
          </div>
        )}

        {/* Ideal for group */}
        {idealFeatures.length > 0 && (
          <div className="flex flex-col gap-[4rem]">
            <motion.h3
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, ease }}
              className="mb-0 text-[3rem] text-center font-medium leading-[1.2] tracking-[-0.15rem] text-black"
            >
              {data.instantIdealTitle ?? 'Ideal for'}
            </motion.h3>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-[2rem]">
              {idealFeatures.map((feature, i) => (
                <FeatureCard
                  key={feature._key ?? i}
                  label={feature.label}
                  icon={feature.icon}
                  index={i}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
